/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 個人設定（プロフィール）コンポーネント。自分のユーザー情報（GitHub ユーザー ID）を取得・編集・保存する
 * @graph-connects useProfile [calls] 自分のプロフィール取得
 * @graph-connects useUpdateProfile [calls] プロフィール更新ミューテーション
 * @graph-connects useToast [calls] Toast 通知
 */
import { useState } from 'react';
import { useProfile, useUpdateProfile } from '../../hooks/index.js';
import { useToast } from '@cortex/web-design-system/components/ui/Toast';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { ApiError } from '../../lib/api.js';
import type { Profile } from '../../types/profile.js';

/**
 * 個人設定フォーム。GitHub ユーザー ID の入力・保存を行い、成功/失敗を Toast で通知する。
 * プロフィール読み込み完了後にのみマウントされるため、初期値を useState で確定できる。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 個人設定フォーム本体。GitHub ユーザー ID を入力・保存し、結果を Toast で通知する
 * @graph-connects useUpdateProfile [calls] プロフィール更新ミューテーション
 * @graph-connects useToast [calls] Toast 通知
 */
function ProfileForm({ profile }: { profile: Profile }) {
  const updateProfile = useUpdateProfile();
  const { showToast } = useToast();
  const [githubUserId, setGithubUserId] = useState(profile.githubUserId ?? '');

  const trimmed = githubUserId.trim();
  const isDirty = trimmed !== (profile.githubUserId ?? '');

  return (
    <Card className="max-w-xl p-4 sm:p-6">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          updateProfile.mutate(
            { githubUserId: trimmed === '' ? null : trimmed },
            {
              onSuccess: () => {
                showToast('success', '個人設定を保存しました');
              },
              onError: (err) => {
                showToast('error', err instanceof ApiError ? err.message : '保存に失敗しました');
              },
            },
          );
        }}
        className="space-y-4"
      >
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">ユーザー情報</h2>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            自分のユーザー情報を設定します
          </p>
        </div>
        <Input
          label="GitHub ユーザー ID"
          value={githubUserId}
          onChange={(e) => {
            setGithubUserId(e.target.value);
          }}
          placeholder="octocat"
          maxLength={39}
          fullWidth
          autoComplete="off"
          helperText="GitHub のログイン名（@ は不要）。空欄で保存すると登録を解除します"
        />
        <div className="flex justify-end">
          <Button type="submit" loading={updateProfile.isPending} disabled={!isDirty}>
            保存
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * 個人設定セクション。プロフィールを取得し、読み込み完了後に編集フォームを表示する。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 個人設定セクション。自分のプロフィールを取得し、読み込み状態に応じて skeleton / エラー / 編集フォームを出し分ける
 * @graph-connects useProfile [calls] 自分のプロフィール取得
 * @graph-connects ProfileForm [calls] 個人設定フォーム表示
 */
export function ProfileSettings() {
  const { data: profile, isLoading, isError } = useProfile();

  if (isLoading) {
    return (
      <Card className="max-w-xl p-4 sm:p-6">
        <div className="h-5 w-32 animate-pulse rounded bg-[var(--color-bg-elevated)]" />
        <div className="mt-4 h-10 w-full animate-pulse rounded bg-[var(--color-bg-elevated)]" />
      </Card>
    );
  }

  if (isError || !profile) {
    return (
      <p className="text-sm text-[var(--color-text-muted)]">個人設定の読み込みに失敗しました</p>
    );
  }

  return <ProfileForm profile={profile} />;
}
